import React, { useEffect, useState } from 'react';
import { Form, FormInstance, Input } from 'antd';

// Custom input component for Form.Item to properly trigger onChange
interface PackageSizeInputProps {
  value?: PackageSizeFormValues;
  onChange?: (value: PackageSizeFormValues) => void;
  form: FormInstance;
  onPackageSizeChange: (size: string) => void;
  packageSizeOptions: PackageSizeOption[];
  selectedSize: PackageSizeFormValues;
  setSelectedSize: (size: PackageSizeFormValues) => void;
  t: (key: string) => string;
}

const PackageSizeInput: React.FC<PackageSizeInputProps> = ({
  value,
  onChange,
  form,
  onPackageSizeChange,
  packageSizeOptions,
  selectedSize,
  setSelectedSize,
  t,
}) => {
  const handleSelect = (size: PackageSizeFormValues) => {
    setSelectedSize(size);
    if (onChange) {
      onChange(size);
    }
    onPackageSizeChange(size);
  };

  return (
    <div className='grid grid-cols-2 gap-2'>
      {packageSizeOptions.map((option) => (
        <PackageSize
          key={option.key}
          packageSizeKey={option.key}
          title={option.title}
          description={t(option.description)}
          icon={option.icon}
          selectedValue={value || selectedSize}
          onSelect={handleSelect}
          form={form}
        />
      ))}
    </div>
  );
};

import { Card } from '../Card';
import { PackageSize } from '../PackageSize';
import { Heading } from '../Heading';
import { useTranslation } from 'react-i18next';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { BoxLargeIcon, BoxMediumIcon, BoxSmallIcon, BoxXLargeIcon } from '../Icons';
import { OrderOption, OrderOptionEnum } from '@/types';
import FormCheckbox from './FormCheckbox';
import FragileIcon from '../Icons/fragileIcon';
import MultiBoxSelector, {
  BoxesPriceSummary,
  BoxFeeBreakdown,
  largestSize,
  MAX_BOXES,
  MIN_BOXES,
} from './MultiBoxSelector';
import { ArrowLeftRight, Info, Minus, Plus, Snowflake, Tag } from 'lucide-react';

export type PackageSizeFormValues = 'XS' | 'S' | 'M' | 'L' | 'XL';
interface PackageSizeOption {
  key: PackageSizeFormValues;
  description: string;
  title: string;
  icon: React.FC; // Icon component for size
}

export enum PackageSizeEnum {
  XS = 'extra_small',
  S = 'small',
  M = 'medium',
  L = 'large',
  XL = 'x_large',
}

export const packageSizeOptions: PackageSizeOption[] = [
  { title: 'extra_small', key: 'XS', description: 'up_to_half_kg', icon: BoxSmallIcon },
  { title: 'small', key: 'S', description: 'up_to_one_kg', icon: BoxSmallIcon },
  { title: 'medium', key: 'M', description: 'up_to_three_kg', icon: BoxMediumIcon },
  { title: 'large', key: 'L', description: 'up_to_five_kg', icon: BoxLargeIcon },
  { title: 'x_large', key: 'XL', description: 'up_to_*_kg', icon: BoxLargeIcon },
];

interface PackageSizeCardProps {
  form: FormInstance; // Accept form instance from parent
  onPackageSizeChange: (size: string) => void; // New prop to handle size change
  packageSizeProp?: string;
  orderStatus?: string;
  orderOptions?: OrderOption[];
  clueVerificationLink?: string;
  idCheck?: boolean;
  hasFragileProduct?: boolean;
  /** §5.2 per-box figures from the price preview, once one has landed. */
  boxBreakdown?: BoxFeeBreakdown[];
}

const PackageSizeEditCard: React.FC<PackageSizeCardProps> = ({
  form,
  onPackageSizeChange,
  packageSizeProp,
  orderStatus,
  orderOptions,
  idCheck,
  clueVerificationLink,
  hasFragileProduct = false,
  boxBreakdown,
}) => {
  const [selectedSize, setSelectedSize] = useState<PackageSizeFormValues>('S');
  const watchedBoxes = Form.useWatch('boxes', form) as
    | { size: PackageSizeFormValues }[]
    | undefined;
  const boxes = Array.isArray(watchedBoxes) && watchedBoxes.length >= MIN_BOXES ? watchedBoxes : [];
  const count = Math.max(boxes.length, 1);
  const [mixedChosen, setMixedChosen] = useState(false);
  const sizesDiffer = new Set(boxes.map((b) => b.size)).size > 1;
  // An order that already has boxes of different sizes (edit page, back-nav)
  // opens in the per-box list — and STAYS there. Derived-only, the list
  // collapsed back to the size tiles the moment two boxes became equal,
  // mid-edit, under tiles still highlighting the old size.
  useEffect(() => {
    if (sizesDiffer) setMixedChosen(true);
  }, [sizesDiffer]);
  const mixed = count >= MIN_BOXES && (mixedChosen || sizesDiffer);
  const [isFragileChecked, setIsFragileChecked] = useState(false);
  const [isTemperatureChecked, setIsTemperatureChecked] = useState(false);
  const [isIdChecked, setIsIdChecked] = useState(false);
  const [isClueVerificationChecked, setIsClueVerificationChecked] = useState(false);
  const [hasIdChecked, setHasIdChecked] = useState(false);
  const [hasClueVerification, setHasClueVerification] = useState(false);
  const { t } = useTranslation();

  /*
   * BOXY-301 §5.1 — the box COUNT drives `boxes`. One box sends no `boxes`
   * key at all (the backend refuses a one-element list, and a single-box
   * request must stay byte-identical to before). Same-size mode writes N
   * copies of the tile size; the per-box list adds a copy of the last box.
   */
  const setCount = (next: number) => {
    const n = Math.min(Math.max(next, 1), MAX_BOXES);
    if (n < MIN_BOXES) {
      const keep = boxes.length ? largestSize(boxes) : selectedSize;
      setMixedChosen(false);
      setSelectedSize(keep);
      form.setFieldsValue({ boxes: undefined, packageSize: keep });
      onPackageSizeChange(keep);
      return;
    }
    const next_ = mixed
      ? n > boxes.length
        ? [
            ...boxes,
            ...Array.from({ length: n - boxes.length }, () => ({ ...boxes[boxes.length - 1] })),
          ]
        : boxes.slice(0, n)
      : Array.from({ length: n }, () => ({ size: selectedSize }));
    form.setFieldValue('boxes', next_);
    // setFieldValue does not fire onValuesChange: re-quote explicitly.
    onPackageSizeChange(selectedSize);
  };

  // Same-size mode: a new tile size applies to every box.
  const handleSizeChange = (size: string) => {
    if (count >= MIN_BOXES && !mixed) {
      form.setFieldValue(
        'boxes',
        boxes.map(() => ({ size: size as PackageSizeFormValues })),
      );
    }
    // After the boxes: the quote reads them, and read the old sizes before.
    onPackageSizeChange(size);
  };

  const setMixed = (on: boolean) => {
    setMixedChosen(on);
    if (!on) {
      const size = largestSize(boxes);
      setSelectedSize(size);
      form.setFieldsValue({ packageSize: size, boxes: boxes.map(() => ({ size })) });
      onPackageSizeChange(size);
    }
  };

  const handleFragileChange = (e: CheckboxChangeEvent) => {
    setIsFragileChecked(e.target.checked);
  };

  const handleTemperatureChange = (e: CheckboxChangeEvent) => {
    setIsTemperatureChecked(e.target.checked);
  };

  // TEMP-5: the flag is editable only while the order is New or Scheduled
  // (pattern: OrderTypeCard status gate). No orderStatus (create flow) = enabled.
  const isTemperatureLocked = !!orderStatus && orderStatus !== 'new' && orderStatus !== 'scheduled';

  const handleIdChange = (e: CheckboxChangeEvent) => {
    setIsIdChecked(e.target.checked);
  };

  const handleClueVerificationChange = (e: CheckboxChangeEvent) => {
    setIsClueVerificationChecked(e.target.checked);
  };

  // Sync the selected size when the form initializes or updates
  useEffect(() => {
    const initialSize: PackageSizeFormValues | undefined =
      packageSizeProp || form.getFieldValue('packageSize');

    if (initialSize) {
      setSelectedSize(initialSize);
      form.setFieldsValue({ packageSize: initialSize });
    }
  }, [form, packageSizeProp]);

  useEffect(() => {
    const idCheckedValue = orderOptions?.findIndex((opt) => opt.type === OrderOptionEnum.ID_CHECK);
    const clueVerificationValue = orderOptions?.findIndex(
      (opt) => opt.type === OrderOptionEnum.CLUE_VERIFICATION,
    );

    // Show verification options only in bulk import modal (orderOptions is undefined).
    // Hide in create order (empty array) and edit order (populated array).
    setHasIdChecked(orderOptions === undefined);
    setHasClueVerification(orderOptions === undefined);

    if (idCheckedValue !== undefined || idCheck) {
      setIsIdChecked(idCheckedValue !== -1 || !!idCheck);
    }
    if (clueVerificationValue !== undefined || clueVerificationLink) {
      setIsClueVerificationChecked(clueVerificationValue !== -1 || !!clueVerificationLink);
    }
  }, [orderOptions, idCheck, clueVerificationLink]);

  return (
    <>
      <Card>
        <Heading
          title={t('package_size')}
          fontSize='text-xs'
          fontWeight='font-bold'
          color='text-sd-muted-foreground'
          extraClassName='uppercase tracking-wider mb-4'
        />
        {/* Same size: the tiles set every box. Different sizes: the tiles give
            way to the per-box list — `orders.size` is derived from the largest
            box, so two size controls would be two answers to one question. */}
        {mixed ? (
          <Form.Item name='boxes' noStyle>
            <MultiBoxSelector breakdown={boxBreakdown} />
          </Form.Item>
        ) : (
          <>
            {/* Keeps `boxes` registered, so a value the edit page hydrates is
                seen by the watcher above. */}
            <Form.Item name='boxes' hidden noStyle />
            <Form.Item name='packageSize' noStyle>
              <PackageSizeInput
                form={form}
                onPackageSizeChange={handleSizeChange}
                packageSizeOptions={packageSizeOptions}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                t={t}
              />
            </Form.Item>
          </>
        )}

        <div className='mt-4 flex items-center justify-between border-0 border-t border-solid border-sd-border pt-4'>
          <span className='text-xs font-bold uppercase tracking-wider text-sd-muted-foreground'>
            {String(t('number_of_boxes'))}
          </span>
          <div className='flex items-center gap-1'>
            {[
              { delta: -1, Icon: Minus, label: t('fewer_boxes'), off: count <= 1 },
              { delta: 1, Icon: Plus, label: t('more_boxes'), off: count >= MAX_BOXES },
            ].map(({ delta, Icon, label, off }, idx) => (
              <React.Fragment key={delta}>
                {idx === 1 ? (
                  <span
                    className='w-8 text-center text-sm font-semibold tabular-nums text-sd-foreground'
                    data-testid='box-count'
                  >
                    {count}
                  </span>
                ) : null}
                <button
                  type='button'
                  aria-label={String(label)}
                  disabled={off}
                  onClick={() => setCount(count + delta)}
                  className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-solid border-sd-input bg-sd-card text-sd-foreground transition-colors hover:border-sd-primary/50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-sd-input'
                >
                  <Icon className='h-3.5 w-3.5' />
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {count >= MIN_BOXES ? (
          <div className='mt-3'>
            <div className='flex items-center justify-between gap-2'>
              <span className='text-sm font-semibold text-sd-foreground'>
                {mixed
                  ? String(t('n_boxes', { count }))
                  : `${count} × ${String(
                      t(packageSizeOptions.find((o) => o.key === selectedSize)?.title ?? ''),
                    )}`}
              </span>
              <span
                title={String(t('each_box_gets_label', { count }))}
                className='inline-flex shrink-0 items-center gap-1 rounded-md bg-sd-muted px-2 py-0.5 text-[10px] font-semibold tabular-nums text-sd-muted-foreground'
              >
                <Tag className='h-3 w-3' />
                {`BOX 1/${count} … ${count}/${count}`}
              </span>
            </div>
            <p className='mb-0 mt-1.5 flex items-start gap-1.5 text-xs leading-4 text-sd-muted-foreground'>
              <Info className='mt-px h-3.5 w-3.5 shrink-0' />
              {String(t('additional_boxes_reduced_rate'))}
            </p>
            <BoxesPriceSummary breakdown={boxBreakdown} count={count} />
            <button
              type='button'
              onClick={() => setMixed(!mixed)}
              className='mt-3 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-semibold text-sd-foreground underline-offset-2 hover:underline'
            >
              <ArrowLeftRight className='h-3.5 w-3.5' />
              {String(t(mixed ? 'boxes_all_same_size' : 'boxes_different_sizes'))}
            </button>
          </div>
        ) : null}
      </Card>
      <Card>
        <Heading
          title={t('handling')}
          fontSize='text-xs'
          fontWeight='font-bold'
          color='text-sd-muted-foreground'
          extraClassName='uppercase tracking-wider mb-4'
        />
        <div className='space-y-3'>
          <FormCheckbox
            name='isFragile'
            checked={isFragileChecked}
            onChange={handleFragileChange}
            label={t('package_content_is_fragile')}
            form={form}
            show={true}
            icon={<FragileIcon width={16} height={16} />}
            disabled={hasFragileProduct}
          />
          <FormCheckbox
            name='temperatureControl'
            checked={isTemperatureChecked}
            onChange={handleTemperatureChange}
            label={String(t('temperature_control'))}
            form={form}
            show={true}
            icon={
              // TEMP-12 — icon parity with the fragile row above: same 16px
              // slot in the FormCheckbox label, Snowflake = the cold-chain
              // glyph used across list/summary/details (stroke inherits the
              // label's text-sd-foreground via currentColor).
              <span
                data-testid='temperature-control-checkbox-icon'
                className='inline-flex shrink-0'
              >
                <Snowflake className='h-4 w-4' strokeWidth={2} />
              </span>
            }
            disabled={isTemperatureLocked}
          />
          {isTemperatureLocked ? (
            <p className='text-xs leading-4 text-sd-muted-foreground ps-6 mb-0'>
              {String(t('temperature_control_locked_hint'))}
            </p>
          ) : null}
          <FormCheckbox
            name='isIdChecked'
            checked={isIdChecked}
            onChange={handleIdChange}
            label={t('id_checked')}
            form={form}
            show={hasIdChecked}
          />

          <FormCheckbox
            name='isClueVerification'
            checked={isClueVerificationChecked}
            onChange={handleClueVerificationChange}
            label={t('clue_verification')}
            form={form}
            show={hasClueVerification}
          />
          {hasClueVerification && (
            <Form.Item
              name='clueLink'
              className='w-full mb-0'
              rules={[
                ...(isClueVerificationChecked
                  ? [{ required: true, message: t('this_field_is_required') }]
                  : []),
                {
                  type: 'url' as const,
                  message: t('please_enter_a_valid_url'),
                },
              ]}
            >
              <Input
                placeholder={t('verification_url_optional')}
                className='border w-full border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md hover:border-sd-input focus:border-sd-input focus:shadow-none'
              />
            </Form.Item>
          )}
        </div>
      </Card>
    </>
  );
};

export default PackageSizeEditCard;
